import React from 'react';
import { Link } from 'react-router-dom';
import { resolveActivityText, tActivity } from '@/i18n/activity';
import type { CustomerActivityItem } from '@/api/services/customerActivity';
import type { ActivityActionCapabilities } from '@/utils/activityActions';
import {
  buildActivityMetaLine,
  formatActivityCategoryLabel,
  resolveActivityChipVariant,
} from './activityCardHelpers';

function relativeTime(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

type Props = {
  item: CustomerActivityItem;
  onMarkRead?: (id: string) => void;
  compact?: boolean;
  actions?: ActivityActionCapabilities;
};

export default function ActivityCard({ item, onMarkRead, compact = true, actions }: Props) {
  const { title, summary } = resolveActivityText(item);
  const chipVariant = resolveActivityChipVariant(item);
  const categoryLabel = formatActivityCategoryLabel(item.category);
  const metaLine = buildActivityMetaLine(item, relativeTime(item.createdAt));
  const usageUrl = actions?.canViewUsage ? item.metadata?.actionUrl : undefined;
  const usageLabel = String(item.metadata?.actionLabel || 'View usage');
  const showRetry =
    Boolean(actions?.canRetry) &&
    Boolean(item.metadata?.retryUrl || item.type?.includes('failed'));

  const markIfUnread = () => {
    if (!item.read) onMarkRead?.(item.id);
  };

  return (
    <article
      className={[
        'activity-item',
        compact ? 'activity-item--compact' : '',
        item.read ? '' : 'activity-item--unread',
        `activity-item--severity-${item.severity}`,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="activity-item__main">
        <div className="activity-item__title-row">
          <span className="activity-item__dot" aria-hidden />
          <strong className="activity-item__title">{title}</strong>
          {!item.read ? (
            <span className="activity-chip activity-chip--new">NEW</span>
          ) : null}
          <span className={`activity-chip activity-chip--${chipVariant === 'new' ? 'default' : chipVariant}`}>
            {categoryLabel}
          </span>
          {summary ? <span className="activity-item__summary muted">{summary}</span> : null}
        </div>
        <div className="activity-item__meta muted">{metaLine}</div>
      </div>
      <div className="activity-item__actions">
        {usageUrl ? (
          <Link className="btn btn-sm btn-ghost" to={String(usageUrl)} onClick={markIfUnread}>
            {usageLabel}
          </Link>
        ) : null}
        {showRetry && item.metadata?.retryUrl ? (
          <Link className="btn btn-sm btn-ghost" to={String(item.metadata.retryUrl)} onClick={markIfUnread}>
            {tActivity('activity.action.retry')}
          </Link>
        ) : null}
        {item.link ? (
          <Link className="btn btn-sm" to={item.link} onClick={markIfUnread}>
            {tActivity('activity.action.open')}
          </Link>
        ) : null}
        {!item.read && onMarkRead ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onMarkRead(item.id)}>
            Mark read
          </button>
        ) : null}
      </div>
    </article>
  );
}
